import { LegTableView } from '#/unified-rfqs/ui/types';

import { Bbo } from '#/unified-rfqs/entities/drfq/bbo';
import { DrfqLeg } from '#/unified-rfqs/entities/drfq/domain';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { QuoteBuilderState, TwoWayValue } from './form-reducer';
import timerView from './views/timer';
import quantityView from './views/quantity';
import priceView from './views/price';
import legsView from './views/legs';
import validationView from './views/validation';
import legsPricesView, { LegsPricesView } from './views/legsPrices';

import bestAskBidQuoteView, {
  BestAskBidQuoteView,
} from './views/bestAskBidQuote';

interface FormOption<T> {
  readonly value: T;
  readonly label: string;
  readonly isDefault?: boolean;
}

export interface View {
  readonly timer: {
    readonly options: readonly FormOption<string>[];
    readonly selectedOption?: FormOption<string>;
  };

  readonly price: {
    readonly value: TwoWayValue;
    readonly precision: number;
  };
  readonly quantity: {
    readonly value: string;
    readonly precision: number;
    readonly error: string | undefined;
  };

  readonly account: string | undefined;
  readonly legs: readonly LegTableView[];
  readonly legsPrices: LegsPricesView;
  readonly bestQuote: BestAskBidQuoteView;

  readonly fieldsValidation: {
    readonly price: boolean;
    readonly quantity: boolean;
  };

  readonly isValid: boolean;

  readonly isPrefillingQuotes: boolean;

  readonly isSingleLeg: boolean;
  readonly isAnonymousRfq: boolean;
  readonly markPriceIv: string;
}

export const [useQuoteBuilderView, QuoteBuilderViewProvider] =
  createSafeContext<View>('QuoteBuilderView');

interface ViewParams {
  readonly bbo: Bbo | null;
  readonly legPricingEnabled: boolean;
  readonly state: QuoteBuilderState;
  readonly legs: readonly DrfqLeg[] | undefined;
  readonly myDesk: string;
  readonly isPrefillingQuotes: boolean;
  readonly isLegPricesLoading: boolean;
  readonly productCodes: string[];
  readonly isAnonymousRfq: boolean;
}

export function createView(params: ViewParams): View {
  const {
    bbo,
    state,
    legs = [],
    legPricingEnabled,
    isPrefillingQuotes,
    isLegPricesLoading,
    productCodes,
    isAnonymousRfq,
  } = params;

  const { account, price, quantity, quoteTimer, legsPrice } = state;
  const productCode = productCodes[0];

  const fieldsValidation = validationView({
    legsPrice,
    legs,
    price,
    legPricingEnabled,
    quantity,
    productCode,
  });

  const nonHedgeLegs = legs.filter((leg) => !leg.isHedge);
  const isSingleLeg = nonHedgeLegs.length === 1;

  return {
    timer: timerView(quoteTimer),
    account,
    quantity: quantityView(quantity, legs),
    price: priceView({
      price,
      legPricingEnabled,
      legsPrice,
      legs,
      productCode,
    }),
    legs: legsView(legs),
    legsPrices: legsPricesView(legsPrice, bbo, legs, isLegPricesLoading),
    bestQuote: bestAskBidQuoteView(bbo, legs),
    fieldsValidation,
    isValid:
      fieldsValidation.price &&
      fieldsValidation.quantity &&
      account != null &&
      account !== '',
    isPrefillingQuotes,
    isSingleLeg,
    isAnonymousRfq,
    markPriceIv: bbo?.legs[0]?.mark_price_iv ?? '',
  };
}
